"use client";

import { createContext, useEffect, useState } from "react";
import { usePathname } from "next/navigation";

const themes: Record<string, Record<string, string>> = {
  default: {
    primary: "#1a51aa",
    secondary: "#1a51aa",
    text: "#1a51aa",
    "header-bg": "#1a51aa",
    "primary-alpha": "26 81 170",
  },
  green: {
    primary: "#006B66",
    secondary: "#006B66",
    text: "#006B66",
    "header-bg": "#006B66",
    "primary-alpha": "0 107 102",
  },
  darkBlue: {
    primary: "#003D6C",
    secondary: "#003D6C",
    text: "#003D6C",
    "header-bg": "#003D6C",
    "primary-alpha": "0 61 108",
  },
  gold: {
    primary: "#211c02",
    secondary: "#211c02",
    text: "#211c02",
    "header-bg": "#211c02",
    "primary-alpha": "33 28 2",
  },
  simpleBlue: {
    primary: "#0D3F91",
    secondary: "#0D3F91",
    text: "#0D3F91",
    "header-bg": "#0D3F91",
    "primary-alpha": "13 63 145",
  },
  portfolioBlue: {
    primary: "#0D3F91",
    secondary: "#0D3F91",
    text: "#0D3F91",
    "header-bg": "#0D3F91",
    "primary-alpha": "13 63 145",
  },
  aboutUs: {
    primary: "#000342",
    secondary: "#000342",
    text: "#000342",
    "header-bg": "#00034200",
    "primary-alpha": "0 3 66",
  },
  homePage: {
    primary: "#001882",
    secondary: "#001882",
    text: "#001882",
    "header-bg": "#00188200",
    "primary-alpha": "0 24 130",
  },
  transparentDefault: {
    primary: "#1a51aa",
    secondary: "#1a51aa",
    text: "#1a51aa",
    "header-bg": "#1a51aa00",
    "primary-alpha": "26 81 170",
  },
};

type ThemeKey = keyof typeof themes;

type ThemeContextType = {
  theme: ThemeKey;
  setTheme: (theme: ThemeKey) => void;
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({
  initialTheme,
  children,
}: {
  initialTheme: ThemeKey;
  children: React.ReactNode;
}) {
  const [theme, setTheme] = useState<ThemeKey>(initialTheme);
  const pathname = usePathname();

  useEffect(() => {
    const selected = themes[theme] || themes.default;
    Object.entries(selected).forEach(([key, value]) => {
      document.documentElement.style.setProperty(`--${key}`, value);
    });
  }, [theme]);

  useEffect(() => {
    if (
      pathname.startsWith("/tools-and-calculators") ||
      pathname.endsWith("-sip-calculator") ||
      pathname.startsWith("/mutual-fund/portfolio-overlap") ||
      pathname.startsWith("/client-assessment")
    ) {
      setTheme("green");
    } else if (pathname === "/") {
      setTheme("homePage");
    } else if (pathname.startsWith("/financial-planning/retirement-planning")) {
      setTheme("darkBlue");
    } else if (pathname.startsWith("/buy-digital-gold")) {
      setTheme("gold");
    } else if (pathname.startsWith("/portfolio-services")) {
      setTheme("portfolioBlue");
    } else if (pathname === "/sif") {
      setTheme("simpleBlue");
    } else if (pathname === "/about-us") {
      setTheme("aboutUs");
    } else if (pathname === "/contact-us") {
      setTheme("transparentDefault");
    } else {
      setTheme("default");
    }
  }, [pathname]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}
